import { motion } from "framer-motion";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { useServerStatus } from "@/lib/api";

export const Reveal = ({ children, delay = 0, className = "", y = 30 }) => (
  <motion.div initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay, ease: [0.2, 0.8, 0.2, 1] }} className={className}>
    {children}
  </motion.div>
);

export const Wrap = ({ children, className = "" }) => <div className={`mx-auto max-w-[1400px] px-5 sm:px-8 ${className}`}>{children}</div>;

export const SecTitle = ({ n, en, ar, children }) => (
  <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
    <div>
      <div className="flex items-center gap-4">
        <span className="f-mono text-xs text-[var(--n-cyan)]">{n}</span>
        <span className="h-px w-16 bg-[var(--n-cyan)]/50" />
        <span className="kicker">{en}</span>
      </div>
      <h2 className="f-ar mt-4 text-4xl leading-tight sm:text-5xl lg:text-6xl">{ar}</h2>
    </div>
    {children}
  </Reveal>
);

export function PageHero({ en, ar, desc, img, children }) {
  return (
    <section className="relative isolate overflow-hidden border-b border-[var(--n-line)] pb-16 pt-36 sm:pt-44">
      {img && <img src={img} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover opacity-25" />}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#030509]/60 via-[#030509]/80 to-[#030509]" />
      <div className="scan -z-10" />
      <div aria-hidden className="f-lat outline-soft pointer-events-none absolute -bottom-6 left-0 -z-10 select-none whitespace-nowrap text-[22vw] leading-none">{en}</div>
      <Wrap>
        <Reveal>
          <div className="flex items-center gap-4"><span className="h-px w-12 bg-[var(--n-cyan)]" /><span className="kicker">{en}</span></div>
          <h1 className="f-ar mt-5 text-5xl leading-[1.1] sm:text-6xl lg:text-7xl" data-testid="page-title">{ar}</h1>
          {desc && <p className="mt-6 max-w-2xl text-base leading-8 text-[var(--n-mute)]">{desc}</p>}
          {children}
        </Reveal>
      </Wrap>
    </section>
  );
}

export const Points = ({ items = [], className = "" }) => (
  <ul className={`space-y-3 ${className}`}>
    {items.map((t, i) => (
      <li key={i} className="flex gap-3 text-[15px] leading-7 text-slate-300">
        <span className="f-mono mt-0.5 shrink-0 text-xs text-[var(--n-cyan)]">{String(i + 1).padStart(2, "0")}</span><span>{t}</span>
      </li>
    ))}
  </ul>
);

export const Chip = ({ children, tone = "cyan" }) => {
  const t = { cyan: "text-[var(--n-cyan)] border-[var(--n-cyan)]/40", red: "text-[var(--n-red)] border-[var(--n-red)]/50", mute: "text-slate-400 border-slate-600", gold: "text-amber-300 border-amber-300/50" };
  const ar = /[\u0600-\u06FF]/.test(String(children));
  return <span className={`inline-flex items-center border px-2.5 py-1 ${ar ? "text-xs font-bold" : "f-lat text-[11px] tracking-[0.2em]"} ${t[tone]}`}>{children}</span>;
};

export const Skeleton = () => <Wrap className="grid gap-6 py-16 md:grid-cols-3" data-testid="loading">{[0, 1, 2].map((i) => <div key={i} className="h-80 animate-pulse bg-white/[0.03]" />)}</Wrap>;

export const Ticket = ({ href, text = "للطلب افتح تذكرة في ديسكورد المدينة وراح يتم خدمتك مباشرة." }) => (
  <Wrap className="mt-24">
    <Reveal className="hud relative flex flex-col items-start justify-between gap-6 overflow-hidden border border-[var(--n-line)] bg-[var(--n-ink)] p-10 sm:flex-row sm:items-center">
      <div className="siren absolute inset-x-0 top-0" />
      <p className="f-ar text-2xl leading-10 sm:text-3xl">{text}</p>
      <a href={href} target="_blank" rel="noreferrer" className="btn-ice shrink-0" data-testid="ticket-cta-btn">افتح تذكرة في الديسكورد</a>
    </Reveal>
  </Wrap>
);

export function StatusChip() {
  const { data } = useServerStatus();
  return (
    <div className="flex items-center gap-2 border border-[var(--n-line)] bg-black/40 px-3 py-1.5 text-xs" data-testid="nav-status">
      <i className={data?.online ? "dot-live" : "dot-off"} />
      <span className="f-mono" dir="ltr">{data?.players ?? 0}/{data?.max || 128}</span>
    </div>
  );
}

export function StatusPanel() {
  const { data, isLoading } = useServerStatus();
  const pct = data?.max ? Math.min(100, (data.players / data.max) * 100) : 0;
  const copy = async () => {
    try { await navigator.clipboard.writeText(`connect ${data.connect}`); toast.success("انسخ أمر الاتصال، الصقه في F8"); }
    catch { toast.error(`ما قدرنا ننسخ، اكتب: connect ${data.connect}`); }
  };
  return (
    <div className="hud border border-[var(--n-line)] bg-black/50 p-5 backdrop-blur-xl sm:w-[360px]" data-testid="server-status">
      <div className="flex items-center justify-between">
        <span className="kicker !text-[11px]">LIVE · SERVER</span>
        <span className="flex items-center gap-2 text-xs" data-testid="server-status-state"><i className={data?.online ? "dot-live" : "dot-off"} />{isLoading ? "..." : data?.online ? "متصل" : "غير متصل"}</span>
      </div>
      <div className="mt-3 flex items-baseline gap-2" dir="ltr">
        <span className="f-lat text-6xl leading-none text-white" data-testid="server-status-players">{data?.players ?? 0}</span>
        <span className="f-lat text-2xl text-slate-500">/ {data?.max || 128}</span>
      </div>
      <div className="speedbar mt-4"><i style={{ width: `${pct}%` }} /></div>
      {data?.connect && (
        <button onClick={copy} className="mt-4 flex w-full items-center justify-between border border-[var(--n-line)] bg-black/40 px-3 py-2.5 text-xs text-[var(--n-cyan)] transition-colors hover:border-[var(--n-cyan)]" data-testid="server-connect-copy">
          <span className="f-mono" dir="ltr">connect {data.connect}</span><Copy size={14} />
        </button>
      )}
    </div>
  );
}
